// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import PledgeRecord from './pages/PledgeRecord';
import UserMining from './pages/UserMining';
import UserLogin from './pages/UserLogin';
import UserSignup from './pages/UserSignup';
import UserHome from './pages/UserHome';
import Markets from './pages/UserMarkets';
import UserOption from './pages/UserOption';
import UserExchange from './pages/UserExchange';
import UserAssets from './pages/UserAssets';
import UserDeposit from './pages/UserDeposit';
import UserChat from './pages/UserChat';
import UserAssetExchange from './pages/UserAssetExchange';
import ExchangeRecords from './pages/ExchangeRecords';
import DepositDetails from './pages/DepositDetails';
import UserDepositRecords from './pages/UserDepositRecords';
import WithdrawDetails from './pages/WithdrawDetails';
import UserWithdraw from './pages/UserWithdraw';
import WithdrawalRecords from './pages/WithdrawalRecords';
import UserProfile from './pages/UserProfile';
import UserAccountDetails from './pages/UserAccountDetails';
import UserVerification from './pages/UserVerification';
import PreliminaryVerification from './pages/PreliminaryVerification';
import AdvancedVerification from './pages/AdvancedVerification';
import LoginPassword from './pages/LoginPassword';
import TransactionPassword from './pages/TransactionPassword';
import HelpCenter from './pages/HelpCenter';
import OnlineService from './pages/OnlineService';
import UserTradeHistory from './pages/UserTradeHistory';
import FundManagement from './pages/FundManagement';
import VerificationPage from './pages/VerifacationPage';
import ApplyLoan from './pages/ApplyLoan';
import LoanDetails from './pages/LoanDetails';
import AboutUs from './pages/AboutUs';
import TermsOfService from './pages/TermsOfService';
import CorporateInfo from './pages/CorporateInfo';
import authService from './services/authService';

// Real auth check: gate pages based on stored token/cookie
const PrivateRoute = ({ children }) => {
  const isAuth = authService.isAuthenticated();
  return isAuth ? children : <Navigate to='/login' replace />;
};

/*
const PrivateRoute = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  if (user === undefined) return null; // waiting for auth check
  return user ? children : <Navigate to="/login" state={{ from: location }} />;
};
*/

function App() {
  return (
    <AuthProvider>
      <ToastContainer
        position='bottom-left'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
      />
      <Router>
        <Routes>
          <Route path='/login' element={<UserLogin />} />
          <Route path='/signup' element={<UserSignup />} />
          {/* Always land on Home; it's a public page */}
          <Route path='/' element={<Navigate to='/home' replace />} />

          {/* Public Pages */}
          <Route path='/home' element={<UserHome />} />
          <Route path='/markets' element={<Markets />} />
          <Route path='/about-us' element={<AboutUs />} />
          <Route path='/terms-service' element={<TermsOfService />} />
          <Route path='/cooperation' element={<CorporateInfo />} />

          {/* Protected Pages */}
          <Route
            path='/option'
            element={
              <PrivateRoute>
                <UserOption />
              </PrivateRoute>
            }
          />
          <Route
            path='/exchange'
            element={
              <PrivateRoute>
                <UserExchange />
              </PrivateRoute>
            }
          />
          <Route path='/verification' element={<VerificationPage />} />
          <Route path='/fund-management' element={<FundManagement />} />
          <Route path='/pledge-record' element={<PledgeRecord />} />
          <Route path='/mining' element={<UserMining />} />
          <Route
            path='/apply-loan'
            element={
              <PrivateRoute>
                <ApplyLoan />
              </PrivateRoute>
            }
          />
          <Route
            path='/loan-details'
            element={
              <PrivateRoute>
                <LoanDetails />
              </PrivateRoute>
            }
          />
          <Route
            path='/assets'
            element={
              <PrivateRoute>
                <UserAssets />
              </PrivateRoute>
            }
          />
          <Route
            path='/deposit'
            element={
              <PrivateRoute>
                <UserDeposit />
              </PrivateRoute>
            }
          />
          <Route path='/chat' element={<UserChat />} />
          <Route
            path='/exchangeassets'
            element={
              <PrivateRoute>
                <UserAssetExchange />
              </PrivateRoute>
            }
          />
          <Route
            path='/deposit/:coin'
            element={
              <PrivateRoute>
                <DepositDetails />
              </PrivateRoute>
            }
          />
          <Route
            path='/deposit-records'
            element={
              <PrivateRoute>
                <UserDepositRecords />
              </PrivateRoute>
            }
          />
          <Route
            path='/exchange-records'
            element={
              <PrivateRoute>
                <ExchangeRecords />
              </PrivateRoute>
            }
          />
          <Route
            path='/withdraw'
            element={
              <PrivateRoute>
                <UserWithdraw />
              </PrivateRoute>
            }
          />
          <Route
            path='/withdraw-details/:coin'
            element={
              <PrivateRoute>
                <WithdrawDetails />
              </PrivateRoute>
            }
          />
          <Route
            path='/withdrawal-records'
            element={
              <PrivateRoute>
                <WithdrawalRecords />
              </PrivateRoute>
            }
          />
          <Route
            path='/profile'
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path='/account-details'
            element={
              <PrivateRoute>
                <UserAccountDetails />
              </PrivateRoute>
            }
          />
          <Route
            path='/verification'
            element={
              <PrivateRoute>
                <UserVerification />
              </PrivateRoute>
            }
          />
          <Route
            path='/preliminary-verification'
            element={
              <PrivateRoute>
                <PreliminaryVerification />
              </PrivateRoute>
            }
          />
          <Route
            path='/advanced-verification'
            element={
              <PrivateRoute>
                <AdvancedVerification />
              </PrivateRoute>
            }
          />
          <Route
            path='/login-password'
            element={
              <PrivateRoute>
                <LoginPassword />
              </PrivateRoute>
            }
          />
          <Route
            path='/transaction-password'
            element={
              <PrivateRoute>
                <TransactionPassword />
              </PrivateRoute>
            }
          />
          <Route
            path='/help-center'
            element={
              <PrivateRoute>
                <HelpCenter />
              </PrivateRoute>
            }
          />
          <Route
            path='/online-service'
            element={
              <PrivateRoute>
                <OnlineService />
              </PrivateRoute>
            }
          />
          <Route
            path='/history'
            element={
              <PrivateRoute>
                <UserTradeHistory />
              </PrivateRoute>
            }
          />
          <Route
            path='/verify'
            element={
              <Navigate
                to={authService.isAuthenticated() ? '/home' : '/login'}
                replace
              />
            }
          />
          <Route
            path='/verify-otp'
            element={
              <Navigate
                to={authService.isAuthenticated() ? '/home' : '/login'}
                replace
              />
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
